import React, { useEffect, useState } from 'react';
import {
  useGetReportPromoListQuery,
  ReportPromoType,
  reportPromoUrlConfig,
  downloadReportPromoFile,
} from 'entities/reportPromo';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { useDefaultReportPromoTableValues } from 'widgets/ReportPromo/lib/hooks/useDefaultReportPromoTableValues';
import { useCacheData } from 'shared/hooks';
import { CityType } from 'shared/types';
import { columnConfig } from 'widgets/ReportPromo/model';
import { DataTableBackendPagination } from 'shared/ui/Table';
import { DataAutocompleteFilterType, FilterColumn, AutocompleteFilter, FilterDate } from 'shared/ui/Filter';
import debounce from 'lodash.debounce';
import { Dayjs } from 'dayjs';
import { useGetConstantsQuery } from 'features/getParticipants/api/getConstantsApi';
import { BASE_URL } from 'shared/conf/constants';
import { useGetActivitiesQuery } from 'features/getActivities/api/getActivitiesApi';
import { RootState, useAppDispatch } from 'app/store';
import { setCitiesData } from 'features/getCities/model/citiesSlice';
import { useSelector } from 'react-redux';
import { SelectFilter } from 'shared/ui/Filter/ui/SelectFilter';
import { STATUS_OPTIONS } from 'widgets/ReportPromo/const';
import { useGetUserRolesQuery } from 'features/getUserRoles/getUserRolesApi';
import { dateFormatted } from 'shared/helpers/dateFormatted';

type FilterPromoType = {
  search: string;
  dateAfter: string | null;
  dateBefore: string | null;
  activityIds: number[];
  citiesIds: number[];
  rolesIds: number[];
  status: string;
};

export const ReportPromoWidget = () => {
  const dispatch = useAppDispatch();

  const citiesStore = useSelector((state: RootState) => state.cities.data);

  const [fieldVisibility, setFieldVisibility] = useState(columnConfig.map(() => true));
  const [searchActivity, setSearchActivity] = useState('');
  const [searchCities, setSearchCities] = useState('');
  const [filterPromo, setFilterPromo] = useState<FilterPromoType>({
    search: '',
    status: '',
    dateAfter: null,
    dateBefore: null,
    activityIds: [],
    rolesIds: [],
    citiesIds: [],
  });
  const [pagination, setPagination] = useState({
    rowsPerPage: 5,
    page: 0,
  });
  const [isLoadingDownloadTable, setIsLoadingDownloadTable] = useState(false);

  const { data: constantsList } = useGetConstantsQuery(null);

  const { data: activitiesList, isSuccess: isSuccessActivitiesList } = useGetActivitiesQuery({
    page: 1,
    size: 100,
    search: searchActivity,
    activity_type_ids: [],
  });

  const { data: cities, isSuccess: isSuccessCities } = useGetCitiesQuery({
    search: searchCities,
  });

  const { data: roles, isSuccess: isSuccessRoles } = useGetUserRolesQuery(null);

  const { data: ReportPromoListData, refetch } = useGetReportPromoListQuery({
    size: pagination.rowsPerPage,
    page: pagination.page + 1,
    ...filterPromo,
  });

  useEffect(() => {
    if (citiesStore.length === 0) {
      dispatch(setCitiesData(cities || []));
    }
  }, [dispatch, cities]);

  useEffect(() => {
    refetch();
  }, [filterPromo]);

  const onSearch = debounce((search) => {
    setFilterPromo({ ...filterPromo, search });
    setPagination({ ...pagination, page: 0 });
  }, 300);

  const transformActivityData = activitiesList?.items.map(({ id, name }) => ({
    id,
    name: name.ru,
  }));

  const transformCitiesData = cities?.map(({ id, name_ru }) => ({
    id,
    name: name_ru,
  }));

  const transformRolesData = roles?.map(({ id, name_ru }) => ({
    id,
    name: name_ru,
  }));

  const cachedCities = useCacheData<CityType>(citiesStore as CityType[]);
  const filterField = columnConfig.filter((column, index) => fieldVisibility[index]);
  const tableData = useDefaultReportPromoTableValues(
    ReportPromoListData as ReportPromoType,
    cachedCities,
    constantsList,
  );

  const handleTableUpdate = () => {
    setPagination({ ...pagination, page: 0 });
    refetch();
  };

  const handleAutocompleteSelectedChange = (
    values: DataAutocompleteFilterType[],
    key: 'activityIds' | 'citiesIds' | 'rolesIds',
  ) => {
    const selectedIds = values.map(({ id }) => id);
    setFilterPromo({ ...filterPromo, [key]: selectedIds });
  };

  const handleSelectChange = (value: string, key: 'status') => {
    setFilterPromo({ ...filterPromo, [key]: value });
  };

  const handleDateChange = (date: Dayjs | null, dateType: 'dateAfter' | 'dateBefore') => {
    const dateFormat = dateFormatted(date);
    const dateSelect = { [dateType]: dateFormat };
    setFilterPromo({ ...filterPromo, ...dateSelect });
  };

  const handleTableDownload = () => {
    setIsLoadingDownloadTable(true);

    const url = `${BASE_URL}/promo-b2b/admin/promo-b2b/admin/activities/participants/xlsx/?${reportPromoUrlConfig({
      ...filterPromo,
      pagination: false,
    })}`;

    downloadReportPromoFile({ url, setIsLoadingDownloadTable });
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          marginBottom: '10px',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gridGap: '10px',
        }}
      >
        <FilterDate
          label="Дата c"
          handleDateValuesChange={(date) => handleDateChange(date, 'dateAfter')}
        />
        <FilterDate
          label="Дата по"
          handleDateValuesChange={(date) => handleDateChange(date, 'dateBefore')}
        />
        <AutocompleteFilter
          data={transformActivityData || []}
          label="Выбрать акции"
          setSearch={setSearchActivity}
          handleSelectedValuesChange={(values) => handleAutocompleteSelectedChange(values, 'activityIds')}
          isLoading={isSuccessActivitiesList}
          style={{ width: '29%', paddingTop: '8px' }}
        />
        <AutocompleteFilter
          data={transformCitiesData || []}
          label="Выбрать город"
          setSearch={setSearchCities}
          handleSelectedValuesChange={(values) => handleAutocompleteSelectedChange(values, 'citiesIds')}
          isLoading={isSuccessCities}
          style={{ width: '20%', paddingTop: '8px' }}
        />
        <AutocompleteFilter
          data={transformRolesData || []}
          label="Роль"
          handleSelectedValuesChange={(values) => handleAutocompleteSelectedChange(values, 'rolesIds')}
          isLoading={isSuccessRoles}
          style={{ width: '15%', paddingTop: '8px' }}
        />
        <SelectFilter
          data={STATUS_OPTIONS}
          label="Статус"
          onChange={(value) => handleSelectChange(value as string, 'status')}
          style={{ marginTop: '8px', width: '10%' }}
        />
      </div>
      <FilterColumn
        columnConfig={columnConfig}
        fieldVisibility={fieldVisibility}
        setFieldVisibility={setFieldVisibility}
        style={{ marginBottom: '10px', justifyContent: 'flex-end' }}
      />
      <DataTableBackendPagination
        title="Отчет по акциям"
        pagination={pagination}
        setPagination={setPagination}
        total={ReportPromoListData?.total || 0}
        data={tableData}
        columnConfig={filterField}
        search
        onSearch={onSearch}
        buttonsController
        isLoadingDownload={isLoadingDownloadTable}
        onUpdateTable={handleTableUpdate}
        onDownloadTable={handleTableDownload}
      />
    </>
  );
};
